import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { Typography } from "../../../shared/ui";
import { ChatController } from "../model/types";
import { ChatAttemptItem } from "./ChatAttemptItem";
import { ChatMessageItem } from "./ChatMessageItem";
import css from "./ChatMessageList.module.scss";

const NEAR_BOTTOM_PX = 48;
const SKELETON_ROWS = [0, 1, 2, 3, 4];

interface ChatMessageListProps {
  chat: ChatController;
}

export function ChatMessageList({ chat }: ChatMessageListProps) {
  const {
    records,
    pendingAttempts,
    ownSenderMessageIds,
    historyPhase,
    hasMore,
    loadOlder,
    reloadLatest,
    retryAttempt,
  } = chat;

  const { t } = useTranslation();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  /** Высота до вставки older-блока — чтобы не терять позицию чтения. */
  const prevHeightRef = useRef(0);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (pinnedToBottom) {
      container.scrollTop = container.scrollHeight;
    } else {
      container.scrollTop += container.scrollHeight - prevHeightRef.current;
    }
    prevHeightRef.current = container.scrollHeight;
  }, [records, pendingAttempts, pinnedToBottom]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distance =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setPinnedToBottom(distance <= NEAR_BOTTOM_PX);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const isInitialLoading = historyPhase === "loading-initial";
  const isEmpty =
    !isInitialLoading && records.length === 0 && pendingAttempts.length === 0;

  return (
    <div className={css.wrapper}>
      {historyPhase === "failed" && (
        <div className={css.banner}>
          <Typography variant="caption3" color="orange">
            {t("chat.history_failed")}
          </Typography>
          <button
            type="button"
            className={css.bannerAction}
            onClick={reloadLatest}
          >
            <Typography variant="caption3" color="white">
              {t("chat.refresh")}
            </Typography>
          </button>
        </div>
      )}

      <div className={css.scroll} ref={scrollRef}>
        {hasMore && (
          <button
            type="button"
            className={css.loadOlder}
            disabled={historyPhase !== "idle"}
            onClick={loadOlder}
          >
            <Typography variant="caption3" color="grey">
              {historyPhase === "loading-older"
                ? t("chat.loading")
                : t("chat.load_older")}
            </Typography>
          </button>
        )}

        {isInitialLoading && (
          <div className={css.placeholder}>
            {SKELETON_ROWS.map((row) => (
              <div key={row} className={css.skeleton} />
            ))}
          </div>
        )}

        {isEmpty && (
          <div className={css.placeholder}>
            <Typography variant="caption2" color="grey">
              {t("chat.empty")}
            </Typography>
          </div>
        )}

        {records.map((record) => (
          <ChatMessageItem
            key={record.senderMessageId}
            record={record}
            isOwn={ownSenderMessageIds.has(record.senderMessageId)}
          />
        ))}

        {pendingAttempts.map((attempt) => (
          <ChatAttemptItem
            key={attempt.clientMessageId}
            attempt={attempt}
            onRetry={retryAttempt}
          />
        ))}
      </div>

      {!pinnedToBottom && (
        <button
          type="button"
          className={css.jumpToBottom}
          onClick={() => setPinnedToBottom(true)}
        >
          <Typography variant="caption3" color="white">
            {t("chat.jump_to_bottom")}
          </Typography>
        </button>
      )}
    </div>
  );
}
